import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { BasicDisplayUnits, getPressurePumpUnitLabel, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import ProjectContext from 'modules/project/providers/project/context';
import { CustomUnitFields, MetricPressureUnit, UnitSystem } from 'providers/api';
import { clamp } from 'ramda';
import React from 'react';
import { isCustomUnitField, pumpPressureKpaToBar } from 'utils/unitConversions';
import { flushStageTypeLabel } from '../../MineModel/Flushing/flushLabels';
import { flushPumpHandoverDistance } from '../../MineModel/Flushing/flushOutcome';
import { createFrictionModel } from '../../MineModel/Flushing/flushPressure';
import {
  clearFlushSceneState,
  getFlushSceneState,
  subscribeToFlushSceneState,
  updateFlushSceneState
} from '../../MineModel/Flushing/flushSceneState';
import {
  buildFlushLine,
  FlushRouteInput,
  simulateFlush,
  timeAtVolume
} from '../../MineModel/Flushing/flushSolver';
import { FlushResult, FlushSpec, PasteProperties } from '../../MineModel/Flushing/flushTypes';
import { formatMinutes } from './FlushForm/flushFormHelpers';
import { UnitSystemValue, unitSystemRawValue } from './UnitSystemDetail';

// What the scrub needs beyond the flush itself: which control owns the overlay, the line it runs on
// and the paste it displaces.
export interface FlushScrubContext {
  scrubKey: string;
  route: FlushRouteInput;
  paste: PasteProperties;
}

interface FlushScrubProps extends FlushScrubContext {
  spec: FlushSpec;
  result: FlushResult;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
}

const SLIDER_STEPS = 200;

const PREFIX = 'FlushScrub';

const classes = {
  table: `${PREFIX}-table`,
};

const Root = styled('div')(() => ({
  [`& .${classes.table}`]: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      fontSize: '0.9em',
      padding: 0,
      margin: 0,
      paddingBottom: 8,
    },
  },
}));

const FlushScrub = ({ scrubKey, route, paste, spec, result, unitSystemPreference, customUnitFields }: FlushScrubProps) => {
  const projectContext = React.useContext(ProjectContext);
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const metricPressureUnit = projectContext?.state.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal && unitSystemPreference === UnitSystem.Metric;
  const pressureUnitInfo = getPressurePumpUnitLabel(metricPressureUnit);
  const volumeUnitInfo = unitLabels[BasicDisplayUnits.Volume];
  const distanceUnitInfo = unitLabels[BasicDisplayUnits.Distance];
  const volumeAbbr = isCustomUnitField(unitSystemPreference, customUnitFields, volumeUnitInfo.customUnitField)
    ? volumeUnitInfo.imperial.abbreviation
    : volumeUnitInfo.metric.abbreviation;

  const line = React.useMemo(() => buildFlushLine(route), [route]);
  const friction = React.useMemo(() => createFrictionModel(paste), [paste]);
  const maxVolume = result.finalFlushVolume;
  const handoverDistance = flushPumpHandoverDistance(result);
  const sceneState = getFlushSceneState();
  // Until the user drags, sit at the paste-pump handover: the moment the plan is built around.
  const requestedVolume = sceneState.activeFlushKey === scrubKey ? sceneState.waterVolume : result.pastePumpWaterVolume;
  const waterVolume = clamp(0, Math.max(maxVolume, 0), requestedVolume);
  const profile = React.useMemo(
    () => simulateFlush(line, spec, paste, result, waterVolume, friction),
    [line, spec, paste, result, waterVolume, friction],
  );

  const volume = (value: number) => (
    <UnitSystemValue value={value} unitInfo={volumeUnitInfo} unitSystem={unitSystemPreference} decimalPlaces={1} customUnitFields={customUnitFields} />
  );
  const distance = (value: number) => (
    <UnitSystemValue value={value} unitInfo={distanceUnitInfo} unitSystem={unitSystemPreference} decimalPlaces={0} customUnitFields={customUnitFields} />
  );
  const pressure = (kpa: number) => (
    <UnitSystemValue
      value={isKiloPascal ? kpa : pumpPressureKpaToBar(kpa)}
      unitInfo={pressureUnitInfo}
      unitSystem={unitSystemPreference}
      decimalPlaces={isKiloPascal ? 0 : 1}
      customUnitFields={customUnitFields}
    />
  );
  const sliderLabel = (value: number) => {
    const displayed = unitSystemRawValue({ value, unitSystem: unitSystemPreference, unitInfo: volumeUnitInfo, decimalPlaces: 1, customUnitFields });
    return `${displayed} ${volumeAbbr}`;
  };

  const marks = [
    ...(handoverDistance !== undefined
      ? [{ value: result.pastePumpWaterVolume, label: i18n._('Flush pump') }]
      : []),
    ...(result.airStartWaterVolume !== undefined && result.airStartWaterVolume > 0 && result.airStartWaterVolume < maxVolume
      ? [{ value: result.airStartWaterVolume, label: i18n._('Air') }]
      : []),
  ];

  const handleChange = (_event: Event, value: number | number[]) => {
    if (Array.isArray(value)) return;
    updateFlushSceneState({ activeFlushKey: scrubKey, waterVolume: value });
  };

  const rows: [string, React.ReactNode][] = [
    [i18n._('Water pumped'), volume(profile.waterVolume)],
    [i18n._('Water front'), distance(profile.frontDistance)],
    [i18n._('Elapsed time'), formatMinutes(timeAtVolume(result.stages, profile.waterVolume))],
    [i18n._('Driving pump'), flushStageTypeLabel(profile.drivingPump)],
    [i18n._('Required pressure'), pressure(profile.requiredPressureKpa)],
    ...(spec.airFlushEnabled
      ? [[i18n._('Air purge'), profile.airMayStart ? i18n._('Can start') : i18n._('Not yet')] as [string, React.ReactNode]]
      : []),
  ];

  React.useEffect(() => subscribeToFlushSceneState(() => forceUpdate()), []);

  // Publish the state of the line for the HGL overlay, and take the overlay down with the control.
  React.useEffect(() => {
    updateFlushSceneState({ activeFlushKey: scrubKey, waterVolume, profile, airStartDistance: result.airStartDistance, handoverDistance });
  }, [scrubKey, waterVolume, profile, result.airStartDistance, handoverDistance]);

  React.useEffect(() => () => clearFlushSceneState(scrubKey), [scrubKey]);

  if (maxVolume <= 0) return null;

  return (
    <Root>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          <Trans id="Flush progress">Flush progress</Trans>
        </Typography>
        <Typography variant="caption">
          <Trans id="Drag to move the water front; the HGL chart follows.">Drag to move the water front; the HGL chart follows.</Trans>
        </Typography>
      </Box>
      <Box sx={{ px: 1, mb: 2 }}>
        <Slider
          value={waterVolume}
          min={0}
          max={maxVolume}
          step={maxVolume / SLIDER_STEPS}
          marks={marks}
          onChange={handleChange}
          valueLabelDisplay="auto"
          valueLabelFormat={sliderLabel}
          aria-label={i18n._('Water pumped')}
        />
      </Box>
      <Table className={classes.table} aria-label={i18n._('Flush progress table')} size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell component="th" scope="row">{label}</TableCell>
              <TableCell align="right">{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Root>
  );
};

export default FlushScrub;
